import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'

import { useT } from '@/i18n'

interface State {
  error: Error | null
}

/** 顶层错误边界，避免一个组件炸掉整页白屏。 */
export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('渲染出错:', error, info.componentStack)
  }

  render() {
    if (this.state.error) return <Fallback message={this.state.error.message} />
    return this.props.children
  }
}

/**
 * class 组件用不了 hook，兜底界面拆出来做成函数组件。
 *
 * react-refresh 认不出 class 组件，于是把这个文件看成「组件 + 非组件」混着导出。
 * 为这 15 行单开一个文件不值当，关掉这条。
 */
// eslint-disable-next-line react-refresh/only-export-components
function Fallback({ message }: { message: string }) {
  const t = useT()
  return (
    <div className="mx-auto max-w-md px-6 py-20 text-center">
      <h1 className="text-2xl font-extrabold">{t('error.crashTitle')}</h1>
      <p className="mt-2 text-[15px] text-ink-muted">{message}</p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="mt-6 px-corners bg-brand px-5 py-2 font-bold text-on-brand hover:bg-brand-hover"
      >
        {t('error.reload')}
      </button>
    </div>
  )
}
